/**
 * Reading a supplier's credit memo.
 *
 * When material goes back, the supplier sends a credit memo: a document that
 * looks like an invoice with the signs the other way round. Typing it in again
 * is both tedious and the kind of task where a transposed digit survives for
 * months, so this reads the lines off the text and hands them to a person to
 * confirm.
 *
 * Nothing here is trusted. Every supplier lays these out differently and some
 * are scans with no text layer at all, so the parser's job is to fill the form
 * in, not to be right — the same contract as the roof report reader, for the
 * same reason. A missed line costs somebody one row typed by hand. A wrong line
 * quietly improves a job's margin by money that never came back, which is far
 * worse, so anything ambiguous is dropped rather than guessed.
 *
 * Amounts come back positive. A credit memo prints them negative or in a
 * column headed "credit"; the sign is stripped because the rest of the app
 * treats a return as a positive credit and subtracts it.
 */

/** One line read off a credit memo, ready to confirm. */
export interface CreditMemoLine {
  description: string;
  qty: number;
  unitPrice: number;
  /** qty x unitPrice, as printed — kept so a mismatch can be spotted. */
  amount: number;
}

export interface CreditMemo {
  /** The memo's own number, for the credit_note field. */
  creditNumber: string | null;
  /** Net of tax, when the document says so. */
  subtotal: number | null;
  tax: number | null;
  total: number | null;
  /** Worked out from subtotal and tax when both are printed. */
  taxRate: number | null;
  lines: CreditMemoLine[];
}

/** Money as printed: 1,234.56 / (1,234.56) / -1,234.56 / $1,234.56. */
const MONEY = String.raw`\(?-?\$?\s*([\d,]+\.\d{2})\)?`;

/**
 * A unit price, which suppliers print to more places than two — shingles are
 * quoted at 33.3333 a bundle and the extended amount is what rounds.
 */
const UNIT_MONEY = String.raw`\(?-?\$?\s*([\d,]+\.\d{2,5})\)?`;

const num = (raw: string): number => Number(raw.replace(/,/g, ""));

/** A labelled total: "Subtotal 1,234.56". Last match wins — totals sit last. */
function labelled(text: string, labels: readonly string[]): number | null {
  let found: number | null = null;
  for (const label of labels) {
    const re = new RegExp(
      // Totals sit at the far right of a wide column, so the gap between the
      // label and its number is generous — but must not contain another number.
      `${label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}[^\\d(-]{0,60}${MONEY}`,
      "gi",
    );
    for (const m of text.matchAll(re)) found = num(m[1]!);
    if (found !== null) return found;
  }
  return found;
}

/**
 * A line item: a description, then quantity, then two money columns.
 *
 * Requiring both the unit price and the extended amount is what keeps this
 * honest. A row of prose with a number in it will not match; a real line has
 * the shape `<text> <qty> <price> <amount>`, and the two money columns are
 * checked against the quantity before the line is accepted.
 */
const LINE = new RegExp(
  String.raw`^(.{3,80}?)\s+(-?[\d,]+(?:\.\d+)?)\s+${UNIT_MONEY}\s+${MONEY}\s*$`,
);

/** Lines whose text is a total, not a product. */
const NOT_A_PRODUCT =
  /^(sub)?total|^tax\b|^amount|^balance|^credit\b|^invoice|^page\b|^qty\b|^description/i;

export function parseCreditMemo(text: string): CreditMemo {
  const flat = text.replace(/\r/g, "");

  const creditNumber =
    /(?:credit\s*(?:memo|note|invoice)?|cm|rma)\s*(?:#|no\.?|number)?\s*[:-]?\s*([A-Z0-9][A-Z0-9-]{2,20})/i.exec(
      flat,
    )?.[1] ?? null;

  const subtotal = labelled(flat, ["subtotal", "sub total", "merchandise"]);
  const tax = labelled(flat, ["sales tax", "tax"]);
  const total = labelled(flat, [
    "total credit",
    "credit total",
    "grand total",
    "total",
  ]);

  const lines: CreditMemoLine[] = [];
  for (const raw of flat.split("\n")) {
    const line = raw.trim();
    if (!line || NOT_A_PRODUCT.test(line)) continue;

    const m = LINE.exec(line);
    if (!m) continue;

    const description = m[1]!.trim().replace(/\s{2,}/g, " ");
    const qty = Math.abs(num(m[2]!));
    const unitPrice = Math.abs(num(m[3]!));
    const amount = Math.abs(num(m[4]!));
    if (qty <= 0 || amount <= 0) continue;

    // The two money columns have to agree with the quantity, or this is not
    // the row we think it is. A cent of slack for the supplier's rounding.
    if (Math.abs(qty * unitPrice - amount) > 0.02) continue;

    lines.push({ description, qty, unitPrice, amount });
  }

  const taxRate =
    subtotal && subtotal > 0 && tax !== null
      ? Math.round((tax / subtotal) * 10000) / 100
      : null;

  return { creditNumber, subtotal, tax, total, taxRate, lines };
}

/**
 * A credit memo the reader looked at, in the shape `parseCreditMemo` returns.
 *
 * Jonathon, 2026-09-30: "the upload return document didn't work". The text
 * parser above needs each line printed as one row of text, and a scan, a photo
 * or a PDF whose columns come out of order gives it nothing. So the returns
 * upload asks the same reader a supplier's receipt goes to, and its answer is
 * made into a memo here. ADR 0244.
 *
 * Signs are stripped for the same reason as above: a return is a positive
 * credit. A line with no quantity or no money on it is dropped rather than
 * guessed. The reader prints no credit number, so the person types it.
 */
export function creditMemoFromRead(read: {
  lines: readonly {
    description: string;
    qty: number;
    unitPrice: number;
  }[];
  printedSubtotal: number | null;
  printedTax: number | null;
  printedTotal: number | null;
}): CreditMemo {
  const lines: CreditMemoLine[] = read.lines
    .map((line) => {
      const qty = Math.abs(line.qty);
      const unitPrice = Math.abs(line.unitPrice);
      return {
        description: line.description.trim(),
        qty,
        unitPrice,
        amount: Math.round(qty * unitPrice * 100) / 100,
      };
    })
    .filter((line) => line.description && line.qty > 0 && line.amount > 0);

  const subtotal =
    read.printedSubtotal === null ? null : Math.abs(read.printedSubtotal);
  const tax = read.printedTax === null ? null : Math.abs(read.printedTax);
  const total = read.printedTotal === null ? null : Math.abs(read.printedTotal);
  const taxRate =
    subtotal && subtotal > 0 && tax !== null
      ? Math.round((tax / subtotal) * 10000) / 100
      : null;

  return { creditNumber: null, subtotal, tax, total, taxRate, lines };
}
